// Silhouette curve — the growing line that anchors §I cover and feeds §VII bucket
// routing (Field Mapping Spec §I / ticket 38b1…a56f).
//
// The wizard emits per-stage slider objects. A stage is either the flat
// `{ Vitality: n, … }` form (all seven touched) or the explicit `{ values, touched }`
// form (the dataset harness uses the latter for partial-touch personas). Per stage the
// mean is taken over the TOUCHED layers only (÷touched, s446.d4) — an untouched slider
// is "no data": it drops out of the average rather than being coerced to 0 and dividing
// by 7 (which crushed the line toward the middle exactly where the user stayed silent).
// On all-touched input this equals mean(L1..L7), so the frozen all-touched personas are
// byte-stable; only partial-touch (02/03) move. One number per stage.

export const LAYER_KEYS = [
  'Vitality', 'Security', 'Growth', 'Authorship', 'Wanting', 'Yourself', 'Lasting',
];

/** Pull the 7 layer values out of one wizard stage (either input shape). */
export function stageValues(stage) {
  const v = stage && stage.values ? stage.values : stage;
  // Coerce every layer to a finite number: a non-numeric slider (string/boolean) or a
  // missing key must not leak NaN into the mean → path `d`. Number(x ?? 0) only caught
  // null/undefined; Number.isFinite catches the rest, defaulting to 0.
  return LAYER_KEYS.map((k) => {
    const n = Number(v?.[k]);
    return Number.isFinite(n) ? n : 0;
  });
}

/**
 * mean over TOUCHED layers for one stage (÷touched, s446.d4; was mean(L1..L7)).
 * A stage with NO touched layer is "no data" → `null`, NOT 0 (s836.d3). Zero fabricated a
 * value the person never gave and, read as a data point, dragged §VII's personalBase down to
 * a false `positive`. null keeps the hole; the reader that needs a number interpolates it
 * locally via `filled()` (insights/input.js) and never writes the filled value back. The
 * branch is unreachable behind the s3 deck floor (≥2 touched per stage, s495.d1) — defensive.
 */
export function stageMean(stage) {
  const vals = stageValues(stage);
  const mask = stage && stage.values && stage.touched ? stage.touched : null;
  if (!mask) return vals.reduce((a, b) => a + b, 0) / vals.length; // flat form = all touched
  const touched = vals.filter((_, i) => Boolean(mask[LAYER_KEYS[i]]));
  return touched.length ? touched.reduce((a, b) => a + b, 0) / touched.length : null;
}

/** The silhouette: one mean per stage, in stage order. */
export function silhouetteOf(sliders) {
  return sliders.map(stageMean);
}
